import { Stack, Button, Typography, Box } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BackButton } from '../components/BackButton'
import { PageContainer } from '../components/layout/PageContainer'
import YesNoAction from '../components/YesNoAction'
import { DigitalFarmerAccount, selectCurrentFarmerAccount } from '../features/azure-b2c/storage'
import CartSummary from '../features/cart/components/CartSummary'
import { usePickUpStoreModal } from '../features/cart/components/PickUpStoreModal'
import CartSlice, {
  selectCurrentCart,
  selectOrderStatusById,
  syncCart,
} from '../features/cart/storage/cart.slice'
import { CartItem, CartObject } from '../features/cart/storage/cart.types'
import { useAppDispatch } from '../store/rootStore'
import { TestIds } from '../testIds'
import { useOnlineStatus } from '../util/useOnlineStatus'
import { CartProductCard } from '../features/products/components/ProductCard/CartProductCard'
import { mergeStockWithProducts } from '../features/products/components/StockAvailability/useStockAvailabilities'
import { EditPriceModal } from '../features/cart/components/EditPriceModal'
import { useBranch } from '../util/useBranch'
import { getActivePrices } from '../features/products/storage'
import { merge } from 'lodash'
import { fetchProductDetail } from '../features/products/utils/fetchProductDetail'
import { Loader } from '../components/Loader'
import { generateImageUrl } from '../util/helpers'
import { TFOCartMenu } from '../features/cart/components/TFOCartMenu'
import QuoteProvider from '../features/quotes/components/QuoteModal/useQuoteModal'
import Modal from '../components/Modal'
import { CartColumnView, FlexGrow, FlexShrink } from './CartComponents'

export const Cart = () => {
  const navigate = useNavigate()
  const cart = useSelector(selectCurrentCart)
  const currentAccount = useSelector(selectCurrentFarmerAccount)
  const dispatch = useAppDispatch()
  const isOnline = useOnlineStatus()
  const branchId = useBranch()

  const sync = () => {
    if (cart && currentAccount?.accountNumber && branchId) {
      dispatch(
        syncCart({
          cart: cart,
          branchId,
          accountNumber: currentAccount.accountNumber,
        }),
      )
    }
  }

  const [timer, setTimer] = React.useState<NodeJS.Timeout>()

  React.useEffect(() => {
    if (
      isOnline &&
      (!cart?.syncedAt || new Date(cart?.syncedAt).getTime() < new Date(cart?.updatedAt).getTime())
    ) {
      clearTimeout(timer)
      setTimer(
        setTimeout(() => {
          sync()
        }, 1000),
      )
    }
  }, [cart?.items, currentAccount, isOnline])

  React.useEffect(() => {
    if (cart?.lastConfirmationId) {
      navigate(`/products/checkout/${cart.lastConfirmationId}`, {
        state: { deliveryMode: cart.cartDeliveryMode },
      })
    }
  }, [cart?.lastConfirmationId])

  const [searchParams, setSearchParams] = useSearchParams()

  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (!currentAccount?.accountNumber || busy) {
      return
    }

    const fetchDetailsAndAddToCart = async () => {
      setBusy(true)

      // Extract `items` and `quantities`
      const itemsParam = searchParams.get('ids')
      const quantitiesParam = searchParams.get('qtys')
      const colorsParam = searchParams.get('colors')
      const sizesParam = searchParams.get('sizes')

      // Convert comma-separated strings to arrays
      const itemsArray = itemsParam ? itemsParam.split(',').map(Number) : []
      const quantitiesArray = quantitiesParam ? quantitiesParam.split(',').map(Number) : []
      const colorsArray = colorsParam ? colorsParam.split(',').map(String) : []
      const sizesArray = sizesParam ? sizesParam.split(',').map(String) : []

      // Validate ids and quantities to ensure they are numbers
      const isValidIds = itemsArray.every((id) => !isNaN(id) && Number.isInteger(id) && id > 0)
      const isValidQuantities = quantitiesArray.every(
        (qty) => !isNaN(qty) && Number.isInteger(qty) && qty > 0,
      )

      // Early return if any validation fails
      if (!isValidIds || !isValidQuantities) {
        console.error('Invalid ids or quantities provided. Only positive integers are allowed.')
        return // Exit early if invalid
      }

      // Combine `id` and `quantity` into an array of objects
      const combinedItems = itemsArray.map((id, index) => ({
        id,
        quantity: quantitiesArray[index] || 0, // fallback to 0 if no corresponding quantity
        color: colorsArray[index] || undefined,
        size: sizesArray[index] || undefined,
      }))

      const productsToAddToCart: CartItem[] = []

      try {
        // Use `Promise.all` to fetch all product details concurrently
        const fetchedProducts = await Promise.all(
          combinedItems.map(async (item) => {
            const productDetail = await fetchProductDetail(
              item.id.toString(),
              currentAccount?.accountNumber,
              branchId,
              item.size,
              item.color,
            )

            return {
              ...productDetail,
              quantity: item.quantity,
              productId: item.id,
              masterProductId: item.id,
              imageUrl: generateImageUrl(productDetail.itemNumber),
              // size: item.size,
              // color: item.color,
            } as CartItem
          }),
        )

        productsToAddToCart.push(...fetchedProducts)

        // Now that `productsToAddToCart` is populated, dispatch addToCart actions
        productsToAddToCart.forEach((item) => {
          dispatch(
            CartSlice.actions.addToCart({
              accountNumber: currentAccount?.accountNumber,
              product: item,
            }),
          )
        })
      } catch (error) {
        console.error('Error adding to cart via link:', error)
      } finally {
        // Create a new instance of URLSearchParams to modify
        const newSearchParams = new URLSearchParams(searchParams)

        // Remove only the specific search parameters
        newSearchParams.delete('ids')
        newSearchParams.delete('qtys')
        newSearchParams.delete('colors')
        newSearchParams.delete('sizes')

        // Update the search parameters without affecting other params
        setSearchParams(newSearchParams)

        setTimeout(() => {
          console.log('Add to cart complete')
          setBusy(false)
        }, 200)
      }
    }

    fetchDetailsAndAddToCart()
  }, [searchParams, currentAccount?.accountNumber])

  const cartOrMessage = React.useMemo(() => {
    if (busy) {
      return <Loader title='Adding products to cart' />
    }
    if (currentAccount && cart && cart.items.length > 0) {
      return <YourCart cart={cart} account={currentAccount} />
    } else {
      return (
        <YesNoAction
          bodyProps={{ sx: { mb: 3 } }}
          display='Your Cart'
          body='Your cart is empty. You can add items to your cart from the products page.'
          optionA={{
            label: 'View products',
            link: '/products',
          }}
        />
      )
    }
  }, [cart, busy])

  return (
    <PageContainer data-testid={TestIds.pages.cart}>
      <BackButton text='Products' onClick={() => navigate('/products')} />
      {cartOrMessage}
    </PageContainer>
  )
}

interface YourCartProps {
  cart: CartObject
  account: DigitalFarmerAccount
}
const YourCart = ({ cart, account }: YourCartProps) => {
  const dispatch = useAppDispatch()
  const currentAccount = useSelector(selectCurrentFarmerAccount)

  const [openEditPriceModal, setOpenEditPriceModal] = React.useState(false)
  const [editPriceItem, setEditPriceItem] = React.useState<CartItem>()
  const currentCart = useSelector(selectCurrentCart)
  const orderStatus = useSelector(selectOrderStatusById(currentCart?.offlineCartId))
  const modal = usePickUpStoreModal()
  const isOnline = useOnlineStatus()
  const branchId = useBranch()

  React.useEffect(() => {
    const updateCartItemsWithStockAvailabilities = async () => {
      const updatedCartItems = (await mergeStockWithProducts(cart.items, dispatch)) as CartItem[]
      if (updatedCartItems.length > 0) {
        dispatch(
          CartSlice.actions.setStockAvailability({
            accountNumber: account.accountNumber,
            productWithStockAvailabilities: updatedCartItems,
          }),
        )
      }
    }
    updateCartItemsWithStockAvailabilities()
  }, [cart?.pickupStore])

  const onChangeQuantity = (value: number, item: CartItem) => {
    if (value > 0) {
      dispatch(
        CartSlice.actions.setQuantity({
          accountNumber: account.accountNumber,
          product: item,
          amount: value,
          isOnline: isOnline,
        }),
      )
    }
  }

  const onRevertEditedPrice = async (item: CartItem) => {
    if (currentAccount?.accountNumber) {
      const request = await getActivePrices(
        currentAccount.accountNumber,
        [item.productId],
        branchId,
      )
      const price = request?.data.prices[0]
      if (price) {
        dispatch(
          CartSlice.actions.removeItem({
            accountNumber: currentAccount.accountNumber,
            product: item,
          }),
        )

        const newItem = { ...item, editedPriceExcGst: undefined, lineId: undefined } as CartItem

        dispatch(
          CartSlice.actions.addToCart({
            accountNumber: currentAccount.accountNumber,
            product: merge(newItem, price) as CartItem,
          }),
        )
      }
    }
  }

  const onApplyEditedPrince = (editedPrice: number, editedQuantity: number, item: CartItem) => {
    if (currentAccount?.accountNumber)
      dispatch(
        CartSlice.actions.setEditedPrice({
          accountNumber: currentAccount?.accountNumber,
          product: item,
          editedPrice,
          editedQuantity,
        }),
      )
  }

  const renderEditPriceModal = () => {
    return editPriceItem == null ? null : (
      <EditPriceModal
        item={editPriceItem}
        open={openEditPriceModal}
        setOpen={setOpenEditPriceModal}
        onApplyEditedPrince={onApplyEditedPrince}
        onRevertEditedPrice={onRevertEditedPrice}
      />
    )
  }

  const [clearWarning, setClearWarning] = React.useState(false)
  const areYouSure = () => {
    return (
      <Modal
        open={clearWarning}
        title={'Remove all items from cart?'}
        fullScreen={false}
        handleClose={() => {
          setClearWarning(false)
        }}
        footer={
          <Button
            variant='contained'
            onClick={() => {
              setClearWarning(false)
              clearCart()
            }}
          >
            Clear cart
          </Button>
        }
      />
    )
  }

  const clearCart = () => {
    dispatch(
      CartSlice.actions.clearCart({
        accountNumber: account.accountNumber,
      }),
    )
  }

  return (
    <>
      <CartColumnView>
        <FlexGrow>
          <Stack direction='row' justifyContent={'space-between'}>
            <Typography variant='display' gutterBottom data-testid={TestIds.feat.cart.list.title}>
              Your Cart
            </Typography>

            <TFOCartMenu />
          </Stack>
          {cart.items.map((item) => (
            <CartProductCard
              className={orderStatus?.status === 'pending' ? 'shimmer-container' : ''}
              key={item.lineId ?? item.productId}
              item={{ ...item }}
              onChangeQuantity={(value) => {
                onChangeQuantity(value, item)
              }}
              enableVariants={false}
              handleOpenEditPriceModal={() => {
                setEditPriceItem(item)
                setOpenEditPriceModal(true)
              }}
              priceEditable={account.hasTfoAccess}
            />
          ))}

          <Box ml='auto' mr='auto'>
            <Button variant='underline' color={'greyed'} onClick={() => setClearWarning(true)}>
              Clear cart
            </Button>
          </Box>
          {areYouSure()}
        </FlexGrow>
        <FlexShrink>
          <CartSummary
            handleOpenPickUpStoreModal={() => {
              modal.setOpen(true)
            }}
          />
        </FlexShrink>
      </CartColumnView>
      {account.hasTfoAccess && renderEditPriceModal()}
    </>
  )
}

export default () => QuoteProvider(Cart)
